/* global React */
const { useState, useEffect, useRef, useMemo } = React;

// ─── Hooks ─────────────────────────────────────────────────────────────────
function useInView(opts = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } },
      { rootMargin: "-10% 0px -10% 0px", threshold: 0.15, ...opts }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
}

function Appear({ children, as: Tag = "div", delay = 0, ...rest }) {
  const [ref, inView] = useInView();
  return (
    <Tag
      ref={ref}
      className={`appear ${inView ? "on" : ""} ${rest.className || ""}`}
      style={{ transitionDelay: `${delay}ms`, ...(rest.style || {}) }}
      {...Object.fromEntries(Object.entries(rest).filter(([k]) => k !== "className" && k !== "style"))}
    >
      {children}
    </Tag>
  );
}

// Counter that animates from 0 to target when in view.
// Respects prefers-reduced-motion: jumps straight to the final value.
function Counter({ value, target, format = (n) => n.toLocaleString() }) {
  const [ref, inView] = useInView();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView || target == null) return;
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setN(target);
      return;
    }
    const start = performance.now();
    const dur = 1400;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  return (
    <span ref={ref}>
      {target == null ? value : format(n)}
    </span>
  );
}

// ─── Nav ───────────────────────────────────────────────────────────────────
function Nav({ lang, setLang, onOpenDesborde, content, isDesbordeOpen }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  // close mobile menu on resize-up + on Escape
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setMobileOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const close = () => setMobileOpen(false);
  const jump = (id) => { close(); window.location.hash = id; };

  const labelOpen = lang === "en" ? "Open menu" : "Abrir menú";
  const labelClose = lang === "en" ? "Close menu" : "Cerrar menú";
  const langGroupLabel = lang === "en" ? "Language" : "Idioma";
  const navLabel = lang === "en" ? "Main navigation" : "Navegación principal";

  return (
    <header role="banner">
    <nav className={`nav ${mobileOpen ? "is-mobile-open" : ""}`} aria-label={navLabel}>
      <div className="nav-inner">
        <div className="nav-brand">
          <span className="mark">anita<em>.</em>sevilla<em>.</em>ull</span>
          <span className="role">— UX Lead Designer</span>
        </div>

        {/* desktop / tablet links */}
        <div className="nav-links">
          <a href="#projects">{content.nav.work}</a>
          <a href="#bio">{content.nav.bio}</a>
          <a href="#community" className="nav-link-community">{content.nav.community}</a>
          <button type="button" className="btn btn--ghost contact" onClick={onOpenDesborde}>
            <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--teal)", display: "inline-block" }}></span>
            {content.nav.contact}
          </button>
          <div className="nav-lang" role="group" aria-label={langGroupLabel}>
            <button type="button" className={lang === "es" ? "on" : ""} aria-pressed={lang === "es"} onClick={() => setLang("es")}>ES</button>
            <button type="button" className={lang === "en" ? "on" : ""} aria-pressed={lang === "en"} onClick={() => setLang("en")}>EN</button>
          </div>
        </div>

        {/* mobile burger */}
        <button
          type="button"
          className={`nav-burger ${mobileOpen ? "is-open" : ""}`}
          aria-label={mobileOpen ? labelClose : labelOpen}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          onClick={() => setMobileOpen((v) => !v)}
        >
          <span aria-hidden="true"></span>
        </button>
      </div>

      {/* mobile dropdown */}
      <div id="mobile-menu" className="nav-mobile" aria-hidden={!mobileOpen}>
        <a href="#projects" onClick={close} tabIndex={mobileOpen ? 0 : -1}>{content.nav.work} <span className="arrow" aria-hidden="true">→</span></a>
        <a href="#bio" onClick={close} tabIndex={mobileOpen ? 0 : -1}>{content.nav.bio} <span className="arrow" aria-hidden="true">→</span></a>
        <a href="#community" onClick={close} tabIndex={mobileOpen ? 0 : -1}>{content.nav.community} <span className="arrow" aria-hidden="true">→</span></a>
        <button
          type="button"
          className="nav-mobile-cta"
          tabIndex={mobileOpen ? 0 : -1}
          onClick={(e) => { close(); onOpenDesborde && onOpenDesborde(e); }}
        >
          <span><span aria-hidden="true" style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "var(--teal)", marginRight: 8, verticalAlign: "middle" }}></span>{content.nav.contact}</span>
          <span className="arrow" aria-hidden="true">→</span>
        </button>
        <div className="nav-mobile-foot">
          <span>{langGroupLabel.toLowerCase()}</span>
          <div className="nav-lang" role="group" aria-label={langGroupLabel}>
            <button type="button" className={lang === "es" ? "on" : ""} aria-pressed={lang === "es"} tabIndex={mobileOpen ? 0 : -1} onClick={() => setLang("es")}>ES</button>
            <button type="button" className={lang === "en" ? "on" : ""} aria-pressed={lang === "en"} tabIndex={mobileOpen ? 0 : -1} onClick={() => setLang("en")}>EN</button>
          </div>
        </div>
      </div>
    </nav>
    </header>
  );
}

// ─── Splash ────────────────────────────────────────────────────────────────
// Figma-style assembling: collaborator cursors drag components onto a canvas.
// Components animate in (drop, snap, settle) one by one over ~2s.

const SPLASH_CURSORS = [
  { name: "anita",     color: "oklch(0.60 0.10 195)" }, // teal
  { name: "claude",    color: "oklch(0.62 0.16 30)"  }, // warm orange
  { name: "anonymous", color: "oklch(0.55 0.14 280)" }, // purple
];

// Each component has a target position in % within the canvas, a size in %, a delay, and which cursor places it.
// Cursors are assigned by *zone* so each one moves mostly vertically within its column.
//   cursor 0 (anita)     → left column
//   cursor 1 (claude)    → middle area
//   cursor 2 (anonymous) → right column
const SPLASH_PIECES = [
  { id: "nav",   x:  6, y:  7,  w: 88, h:  8, kind: "bar",     by: 0, t:  400 },
  { id: "hero",  x:  6, y: 19,  w: 56, h: 26, kind: "block",   by: 0, t: 1000 },
  { id: "img",   x: 66, y: 19,  w: 28, h: 26, kind: "img",     by: 2, t: 1600 },
  { id: "tag",   x:  6, y: 49,  w: 22, h:  5, kind: "pill",    by: 1, t: 2100 },
  { id: "btn",   x: 30, y: 48,  w: 18, h:  7, kind: "btn",     by: 1, t: 2500 },
  { id: "row1",  x:  6, y: 60,  w: 28, h: 16, kind: "card",    by: 0, t: 3000 },
  { id: "row2",  x: 36, y: 60,  w: 28, h: 16, kind: "card",    by: 1, t: 3500 },
  { id: "row3",  x: 66, y: 60,  w: 28, h: 16, kind: "card",    by: 2, t: 4000 },
  { id: "foot",  x:  6, y: 80,  w: 88, h:  8, kind: "bar-dim", by: 1, t: 4500 },
];

function fmtDate(lang = "es") {
  const d = new Date();
  if (lang === "en") {
    return d.toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" });
  }
  return d.toLocaleDateString("es-AR", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

function Splash({ content, lang = "es" }) {
  const [placed, setPlaced] = useState(() => new Set());
  // schedule placements
  useEffect(() => {
    const timers = SPLASH_PIECES.map((p) =>
      setTimeout(() => setPlaced((s) => new Set(s).add(p.id)), p.t)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  // animate cursors: each cursor parks above its next piece until placed
  const cursorTargets = useMemo(() => {
    // for each cursor, build a sequence of (time, x, y, action) keyframes
    const byCursor = [[], [], []];
    SPLASH_PIECES.forEach((p) => {
      byCursor[p.by].push(p);
    });
    return byCursor;
  }, []);

  const dateStr = fmtDate(lang);

  return (
    <div className="splash" role="status" aria-live="polite">
      <div className="splash-frame">
        <div className="splash-top">
          <div className="splash-toolbar">
            <span className="splash-dot" style={{ background: "oklch(0.78 0.12 30)" }}></span>
            <span className="splash-dot" style={{ background: "oklch(0.82 0.12 80)" }}></span>
            <span className="splash-dot" style={{ background: "oklch(0.75 0.10 145)" }}></span>
            <span className="splash-file mono">portfolio-anita / draft · Frame 1</span>
            <span className="splash-collab mono">
              {SPLASH_CURSORS.map((c) => (
                <span key={c.name} className="chip" style={{ background: c.color }}>
                  {c.name.slice(0, 2).toUpperCase()}
                </span>
              ))}
            </span>
          </div>

          <div className="splash-body">
            <div className="splash-canvas">
              {/* grid background */}
              <div className="splash-grid-bg" />
              {/* frame label */}
              <span className="splash-frame-label mono">Frame 1 — 1200 × 800</span>

              {/* pieces */}
              {SPLASH_PIECES.map((p) => (
                <SplashPiece key={p.id} piece={p} placed={placed.has(p.id)} />
              ))}

              {/* cursors */}
              {SPLASH_CURSORS.map((c, i) => (
                <SplashCursor key={c.name} cursor={c} pieces={cursorTargets[i]} placed={placed} />
              ))}
            </div>

            <aside className="splash-aside">
              <span className="splash-aside-label mono">loading</span>
              <PctBig />
              <PctBar />
            </aside>
          </div>
        </div>

        <div className="splash-foot">
          <span className="mono splash-date">{dateStr}</span>
          <span className="splash-line">
            {content.tagline.pre} <span className="accent">{content.tagline.accent}</span>{content.tagline.post}
          </span>
        </div>
      </div>
    </div>
  );
}

function usePct(dur = 4500) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      // ease-out so the count decelerates as it approaches 100
      const eased = 1 - Math.pow(1 - p, 2);
      setN(Math.round(eased * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [dur]);
  return n;
}

function PctBig() {
  const n = usePct();
  return (
    <div className="splash-pct-big mono" aria-label={`Loading ${n} percent`}>
      <span className="splash-pct-num">{String(n).padStart(3, "0")}</span>
      <span className="splash-pct-sign">%</span>
    </div>
  );
}

function PctBar() {
  const n = usePct();
  return (
    <div className="splash-pct-bar" aria-hidden="true">
      <div className="splash-pct-bar-fill" style={{ width: `${n}%` }} />
    </div>
  );
}

function SplashPiece({ piece, placed }) {
  // explicit inline styles, no transition (avoids React-inline-style racing)
  const style = placed ? {
    left: `${piece.x}%`,
    top: `${piece.y}%`,
    width: `${piece.w}%`,
    height: `${piece.h}%`,
    opacity: 1,
    transform: "translateY(0) scale(1)",
  } : {
    left: `${piece.x}%`,
    top: `${piece.y}%`,
    width: `${piece.w}%`,
    height: `${piece.h}%`,
    opacity: 0,
    transform: "translateY(-8px) scale(.97)",
  };
  return (
    <div className={`sp-piece ${piece.kind} ${placed ? "is-placed" : "is-ghost"}`} style={style}>
      {placed && piece.kind === "img" && <span className="mono sp-piece-label">image · 4:3</span>}
      {placed && piece.kind === "card" && (
        <React.Fragment>
          <span className="sp-sub" style={{ width: "60%" }}></span>
          <span className="sp-sub" style={{ width: "85%" }}></span>
          <span className="sp-sub" style={{ width: "40%" }}></span>
        </React.Fragment>
      )}
      {placed && piece.kind === "block" && (
        <React.Fragment>
          <span className="sp-sub" style={{ width: "70%", height: 14 }}></span>
          <span className="sp-sub" style={{ width: "90%" }}></span>
          <span className="sp-sub" style={{ width: "75%" }}></span>
        </React.Fragment>
      )}
    </div>
  );
}

function SplashCursor({ cursor, pieces, placed }) {
  // find next not-yet-placed piece, otherwise rest at last position
  const next = pieces.find((p) => !placed.has(p.id)) || pieces[pieces.length - 1];
  // Park cursor a bit above-left of the target
  const tx = next ? next.x + 2 : 50;
  const ty = next ? next.y + 2 : 50;
  return (
    <div
      className="sp-cursor"
      style={{
        left: `${tx}%`,
        top: `${ty}%`,
        color: cursor.color,
      }}
    >
      <svg width="14" height="16" viewBox="0 0 14 16" aria-hidden="true">
        <path d="M1 1 L13 8 L7 9 L9 14 L6 15 L4 10 L1 13 Z" fill="currentColor" stroke="white" strokeWidth="0.8" strokeLinejoin="round" />
      </svg>
      <span className="sp-cursor-tag mono" style={{ background: cursor.color }}>{cursor.name}</span>
    </div>
  );
}

// ─── LangToggle reutilizable para headers de segundo nivel ───────────────
// Mismo control que la nav del home pero compacto para chip-row.
function LangToggle({ lang, setLang }) {
  const groupLabel = lang === "en" ? "Language" : "Idioma";
  return (
    <div className="nav-lang nav-lang--compact" role="group" aria-label={groupLabel}>
      <button
        type="button"
        className={lang === "es" ? "on" : ""}
        aria-pressed={lang === "es"}
        onClick={() => setLang && setLang("es")}
      >ES</button>
      <button
        type="button"
        className={lang === "en" ? "on" : ""}
        aria-pressed={lang === "en"}
        onClick={() => setLang && setLang("en")}
      >EN</button>
    </div>
  );
}

// ─── Breadcrumb reutilizable para headers de casos ────────────────────────
// 2 niveles: "← Home"  →  click vuelve al home
// 3 niveles: "← Home · DJY Pro"  →  click en "Home" vuelve al home;
// click en "DJY Pro" vuelve al caso padre.
function CaseBreadcrumb({ onBackToProjects, parent, onBackToParent, lang = "es" }) {
  const ariaLabel = lang === "en" ? "Breadcrumb" : "Migas de pan";
  return (
    <nav className="case-crumbs mono" aria-label={ariaLabel}>
      <button
        type="button"
        className="case-crumbs-link case-crumbs-link--home"
        onClick={onBackToProjects}
      >
        <span className="case-crumbs-arrow" aria-hidden="true">← </span>
        <span className="case-crumbs-text">Home</span>
      </button>
      {parent && (
        <React.Fragment>
          <span className="case-crumbs-sep" aria-hidden="true"> · </span>
          <button
            type="button"
            className="case-crumbs-link case-crumbs-link--parent"
            onClick={onBackToParent || onBackToProjects}
          >
            <span className="case-crumbs-arrow-mobile" aria-hidden="true">← </span>
            <span className="case-crumbs-text">{parent}</span>
          </button>
        </React.Fragment>
      )}
    </nav>
  );
}

window.Appear = Appear;
window.Counter = Counter;
window.useInView = useInView;
window.Nav = Nav;
window.Splash = Splash;
window.CaseBreadcrumb = CaseBreadcrumb;
window.LangToggle = LangToggle;
